import {
    Box,
    Grid,
    LinearProgress,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { toTitle } from '@utils'

type TextPaneOption<DataObj extends object, DataRow extends keyof DataObj> = {
    display: 'text'
    value: DataObj[DataRow] | string
    custom_name?: string
    /**
     * What this row means here, shown on hover.
     *
     * Words like Active carry a different meaning on nearly every screen in
     * this product, so a row whose label is doing real work says what it
     * means where it is used rather than relying on the reader knowing.
     */
    help?: string
}

type ProgressPaneOption = {
    display: 'progress'
    custom_name?: string
    /** As above. */
    help?: string
    /** Spent, in whatever unit `total` uses. Only the ratio is drawn. */
    value: number
    total: number
    unit?: string
    /**
     * What to print above the bar. Without this the pair is printed as two
     * raw numbers, which for seconds converted to hours reads
     * "0.10277777777777779/1 Hours".
     */
    text?: string
}

type PaneOption<DataObj extends object, DataRow extends keyof DataObj> =
    | TextPaneOption<DataObj, DataRow>
    | ProgressPaneOption

export type PaneOptions<DataObj extends object> = {
    [DataRow in keyof Partial<DataObj>]: PaneOption<DataObj, DataRow>
}

interface Props<T extends object> {
    opts: PaneOptions<T>
    title: string
}

export default function SubPane<DataObj extends object>({
    opts,
    title,
}: Props<DataObj>) {
    const gridItems = []

    for (const title in opts) {
        let value
        if (!opts[title]) continue
        if (opts[title].display === 'progress') {
            const progressOpt = opts[title] as ProgressPaneOption
            value = <ProgressItem progressOpt={progressOpt} />
        } else if (opts[title].display === 'text') {
            const textOpt = opts[title]
            value = (
                <Grid item xs={6} style={{ height: '2ex' }}>
                    <Typography
                        variant="body1"
                        style={{ textAlign: 'right', fontWeight: 'bold' }}
                    >
                        {toTitle(String(textOpt.value) ?? '')}
                    </Typography>
                </Grid>
            )
        }
        if (!value) continue
        gridItems.push(
            <>
                <Grid item xs={6} height={'2ex'}>
                    <RowLabel
                        text={toTitle(opts[title].custom_name ?? title)}
                        help={opts[title].help}
                    />
                </Grid>
                {value}
            </>
        )
    }
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
            }}
        >
            <Paper
                sx={{
                    padding: '1em',
                    height: '100%',
                }}
            >
                <Typography variant="h6" sx={{ textAlign: 'left' }}>
                    {toTitle(title)}
                </Typography>
                <Grid container style={{ rowGap: '0.5em' }}>
                    {...gridItems}
                </Grid>
            </Paper>
        </Box>
    )
}

function RowLabel({ text, help }: { text: string; help?: string }) {
    if (!help) {
        return (
            <Typography variant="body1" style={{ textAlign: 'left' }}>
                {text}
            </Typography>
        )
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body1">{text}</Typography>
            <Tooltip title={help} arrow enterTouchDelay={0} placement="top">
                {/* A span because an svg takes no focus, and this should be
                    reachable by keyboard as well as by pointer. */}
                <span tabIndex={0} style={{ display: 'flex', cursor: 'help' }}>
                    <InfoOutlinedIcon
                        fontSize="small"
                        sx={{ opacity: 0.5 }}
                        aria-hidden
                    />
                </span>
            </Tooltip>
        </Box>
    )
}

interface ProgressItemProps {
    progressOpt: ProgressPaneOption
}
/**
 * How much of the allowance is gone, as a percentage the bar can draw.
 *
 * The filled part is what has been SPENT, so a nearly full bar means nearly
 * out. Usage can pass the cap, and an unclamped value draws past the end of
 * the track, so it stops at 100.
 */
function spentPercent(value: number, total: number): number {
    if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) {
        return 0
    }
    return Math.min(100, Math.max(0, (value / total) * 100))
}

/** Past this much spent, the bar stops being reassuring. */
const NEARLY_SPENT_PERCENT = 75

function barColour(spent: number): 'primary' | 'warning' | 'error' {
    if (spent >= 100) return 'error'
    if (spent >= NEARLY_SPENT_PERCENT) return 'warning'
    return 'primary'
}

function ProgressItem({ progressOpt }: ProgressItemProps) {
    const spent = spentPercent(progressOpt.value, progressOpt.total)

    return (
        <>
            <Grid item xs={6} style={{ height: '2ex' }}>
                <Typography
                    variant="body1"
                    style={{
                        textAlign: 'right',
                        fontWeight: 'bold',
                    }}
                >
                    {progressOpt.text ??
                        `${String(progressOpt.value)}/${String(
                            progressOpt.total
                        )}${progressOpt.unit || ''}`}
                </Typography>
            </Grid>
            <Grid item xs={12} style={{ height: '2ex' }}>
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        justifyContent: 'center',
                        alignContent: 'center',
                    }}
                >
                    <LinearProgress
                        variant="determinate"
                        value={spent}
                        color={barColour(spent)}
                    />
                </Box>
            </Grid>
        </>
    )
}
